import React, { useState, useEffect, useRef } from 'react';
import { apiGet, apiPost } from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Eye, Zap, Lightbulb, AlertTriangle, Target, ArrowRight,
  ChevronLeft, ChevronRight, Loader2, Send, CheckCircle2,
  Shield, TrendingUp, Users, FileText, BarChart3, Star,
} from 'lucide-react';

const FOCUS_MODES = [
  {
    id: 'blind_spots', name: 'Discover Blind Spots', icon: Eye, color: 'blue',
    borderColor: 'border-blue-500/30', bgColor: 'bg-blue-500/5', textColor: 'text-blue-500', hoverBorder: 'hover:border-blue-500/50',
    description: 'Find opportunities, weaknesses and missing information you may not have noticed.',
    examples: ['Hidden customer problems', 'Missed market opportunities', 'Internal bottlenecks', 'Missing capabilities', 'Competitive gaps'],
    button: 'Start Analysis',
  },
  {
    id: 'challenge_assumptions', name: 'Challenge Assumptions', icon: Zap, color: 'amber',
    borderColor: 'border-amber-500/30', bgColor: 'bg-amber-500/5', textColor: 'text-amber-500', hoverBorder: 'hover:border-amber-500/50',
    description: 'Question your current beliefs and validate whether your strategy is actually correct.',
    examples: ['Are we solving the right problem?', 'Are our customers really who we think?', 'Is pricing limiting growth?', 'Are we measuring the right KPIs?', 'What assumptions are we making?'],
    button: 'Challenge My Thinking',
  },
  {
    id: 'reveal_insights', name: 'Reveal Insights', icon: Lightbulb, color: 'emerald',
    borderColor: 'border-emerald-500/30', bgColor: 'bg-emerald-500/5', textColor: 'text-emerald-500', hoverBorder: 'hover:border-emerald-500/50',
    description: 'Connect patterns across your company that humans often overlook.',
    examples: ['Revenue trends', 'Customer behavior', 'Product usage', 'Team performance', 'Market signals'],
    button: 'Reveal Insights',
  },
  {
    id: 'uncover_risks', name: 'Uncover Risks', icon: AlertTriangle, color: 'red',
    borderColor: 'border-red-500/30', bgColor: 'bg-red-500/5', textColor: 'text-red-500', hoverBorder: 'hover:border-red-500/50',
    description: 'Identify future risks before they become expensive mistakes.',
    examples: ['Operational risks', 'Financial risks', 'Market threats', 'AI disruption', 'Competitor risks'],
    button: 'Assess Risks',
  },
  {
    id: 'identify_priorities', name: 'Identify Priorities', icon: Target, color: 'violet',
    borderColor: 'border-violet-500/30', bgColor: 'bg-violet-500/5', textColor: 'text-violet-500', hoverBorder: 'hover:border-violet-500/50',
    description: 'Determine what deserves attention first and where your highest ROI exists.',
    examples: ['Quick wins', 'High impact initiatives', 'Resource allocation', 'Bottlenecks', 'Strategic priorities'],
    button: 'Prioritize Now',
  },
];

// ── Mode Selection Screen ──
function FocusModeSelection({ onSelect, onBack }) {
  const [pastSessions, setPastSessions] = useState([]);
  useEffect(() => { apiGet('/focus/sessions').then(r => r.ok ? r.json() : []).then(setPastSessions).catch(() => {}); }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="focus-modes">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6"><ChevronLeft className="w-4 h-4" /> Back to modes</button>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold mb-2">Sharpen Your Strategic Thinking</h1>
        <p className="text-muted-foreground text-sm">How would you like Odapto to challenge your thinking today?</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {FOCUS_MODES.map(mode => {
          const Icon = mode.icon;
          return (
            <div key={mode.id} className={`p-5 rounded-2xl border-2 ${mode.borderColor} ${mode.bgColor} ${mode.hoverBorder} hover:shadow-lg transition-all group`} data-testid={`focus-mode-${mode.id}`}>
              <div className={`w-10 h-10 rounded-xl ${mode.bgColor} flex items-center justify-center mb-3`}>
                <Icon className={`w-5 h-5 ${mode.textColor}`} />
              </div>
              <h3 className="font-bold mb-1">{mode.name}</h3>
              <p className="text-xs text-muted-foreground mb-3">{mode.description}</p>
              <div className="space-y-1 mb-4">
                {mode.examples.map((ex, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <div className={`w-1 h-1 rounded-full ${mode.textColor} opacity-50`} />
                    {ex}
                  </div>
                ))}
              </div>
              <Button onClick={() => onSelect(mode.id)} className={`w-full text-xs py-2 text-white ${mode.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700' : mode.color === 'amber' ? 'bg-amber-600 hover:bg-amber-700' : mode.color === 'emerald' ? 'bg-emerald-600 hover:bg-emerald-700' : mode.color === 'red' ? 'bg-red-600 hover:bg-red-700' : 'bg-violet-600 hover:bg-violet-700'}`} data-testid={`focus-start-${mode.id}`}>
                {mode.button} <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          );
        })}
      </div>

      {pastSessions.length > 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Previous Analyses</h3>
          <div className="space-y-2">{pastSessions.slice(0, 5).map(s => (
            <div key={s.session_id} className="flex items-center gap-3 p-3 rounded-lg border border-border text-left text-sm">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${s.status === 'completed' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-indigo-500/10 text-indigo-500'}`}>
                {s.status === 'completed' ? <CheckCircle2 className="w-4 h-4" /> : <Target className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{s.mode_name}</p>
                <p className="text-xs text-muted-foreground">{s.persona} &middot; {new Date(s.created_at).toLocaleDateString()}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${s.status === 'completed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'}`}>
                {s.status === 'completed' ? 'Report Ready' : 'Active'}
              </span>
            </div>
          ))}</div>
        </div>
      )}
    </div>
  );
}

// ── Focus Chat / Analysis View ──
function FocusChat({ sessionId, mode, initialQuestion, onReport, onBack }) {
  const modeConfig = FOCUS_MODES.find(m => m.id === mode) || FOCUS_MODES[0];
  const Icon = modeConfig.icon;
  const [messages, setMessages] = useState([{ role: 'assistant', content: initialQuestion }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysisReady, setAnalysisReady] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [qNum, setQNum] = useState(1);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const submit = async () => {
    if (!input.trim() || loading) return;
    const answer = input;
    setMessages(prev => [...prev, { role: 'user', content: answer }]);
    setInput(''); setLoading(true);
    try {
      const res = await apiPost(`/focus/sessions/${sessionId}/answer`, { answer });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.question, data: data.data }]);
        setQNum(data.question_number);
        if (data.analysis_ready) setAnalysisReady(true);
      } else toast.error('Failed to get response. Please try again.');
    } catch { toast.error('Connection error.'); }
    setLoading(false);
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const res = await apiPost(`/focus/sessions/${sessionId}/generate-report`, {});
      if (!res.ok) { toast.error('Failed to start'); setGenerating(false); return; }
      const { job_id } = await res.json();
      for (let i = 0; i < 40; i++) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const pr = await apiGet(`/focus/job/${job_id}`);
          if (pr.ok) {
            const job = await pr.json();
            if (job.status === 'complete') { onReport(job.report, mode); setGenerating(false); return; }
            if (job.status === 'failed') { toast.error(job.error || 'Failed'); setGenerating(false); return; }
          }
        } catch { /* retry */ }
      }
      toast.error('Timed out');
    } catch { toast.error('Connection error'); }
    setGenerating(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 flex flex-col h-full" data-testid="focus-chat">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack} className="p-1.5 rounded-lg hover:bg-muted"><ChevronLeft className="w-4 h-4" /></button>
        <div className={`w-8 h-8 rounded-lg ${modeConfig.bgColor} flex items-center justify-center`}><Icon className={`w-4 h-4 ${modeConfig.textColor}`} /></div>
        <div><h2 className="font-semibold text-sm">{modeConfig.name}</h2><p className="text-[10px] text-muted-foreground">Question {qNum}</p></div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 mb-4">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-2xl rounded-br-sm px-4 py-3' : 'bg-muted rounded-2xl rounded-bl-sm px-4 py-3'}`}>
              {msg.role === 'assistant' && msg.data?.assumption_detected && (
                <div className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/20 inline-block mb-2">Assumption: {msg.data.assumption_detected}</div>
              )}
              {msg.role === 'assistant' && msg.data?.risk_category && (
                <div className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 border border-red-500/20 inline-block mb-2">{msg.data.risk_category}</div>
              )}
              <p className="text-sm leading-relaxed">{msg.content}</p>
            </div>
          </div>
        ))}
        {loading && <div className="flex justify-start"><div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-3"><div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Thinking...</span></div></div></div>}
        <div ref={endRef} />
      </div>

      {analysisReady ? (
        <div className="border-t border-border pt-4 text-center">
          <p className="text-sm text-muted-foreground mb-3">Analysis ready! Generate your detailed report.</p>
          <Button onClick={generateReport} disabled={generating} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6" data-testid="generate-focus-report-btn">
            {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating Report...</> : <><FileText className="w-4 h-4 mr-2" /> Generate Analysis Report</>}
          </Button>
        </div>
      ) : (
        <div className="border-t border-border pt-3">
          <div className="flex items-center gap-2">
            <Input value={input} onChange={e => setInput(e.target.value)} placeholder="Type your answer..." onKeyDown={e => e.key === 'Enter' && submit()} disabled={loading} className="flex-1" dir="ltr" data-testid="focus-answer-input" />
            <Button onClick={submit} disabled={!input.trim() || loading} className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0" data-testid="focus-send-btn"><Send className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Focus Report View ──
const REPORT_SECTIONS = {
  blind_spots: [
    { key: 'blind_spots', title: 'Blind Spots Discovered', icon: Eye, color: 'blue', renderItem: (item) => (
      <div className="p-3 rounded-lg border border-border bg-background">
        <div className="flex items-center gap-2 mb-1"><span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${item.severity === 'high' ? 'bg-red-500/10 text-red-600' : item.severity === 'medium' ? 'bg-amber-500/10 text-amber-600' : 'bg-blue-500/10 text-blue-600'}`}>{item.severity}</span><p className="text-sm font-medium">{item.area}</p></div>
        <p className="text-xs text-muted-foreground">{item.description}</p>
        {item.recommendation && <p className="text-xs mt-1"><strong>Action:</strong> {item.recommendation}</p>}
      </div>
    )},
    { key: 'missing_opportunities', title: 'Missing Opportunities', icon: TrendingUp, color: 'emerald', renderList: true },
    { key: 'unknown_unknowns', title: 'Unknown Unknowns', icon: AlertTriangle, color: 'amber', renderList: true },
  ],
  challenge_assumptions: [
    { key: 'assumptions_tested', title: 'Assumptions Tested', icon: Zap, color: 'amber', renderItem: (item) => (
      <div className="p-3 rounded-lg border border-border bg-background">
        <p className="text-sm font-medium mb-1">{item.assumption}</p>
        <div className="grid gap-1 text-xs"><p className="text-emerald-600">For: {item.evidence_for}</p><p className="text-red-600">Against: {item.evidence_against}</p></div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full mt-1 inline-block ${item.verdict === 'validated' ? 'bg-emerald-500/10 text-emerald-600' : item.verdict === 'disproven' ? 'bg-red-500/10 text-red-600' : 'bg-amber-500/10 text-amber-600'}`}>{item.verdict}</span>
      </div>
    )},
    { key: 'recommended_experiments', title: 'Recommended Experiments', icon: Lightbulb, color: 'blue', renderItem: (item) => (
      <div className="p-3 rounded-lg border border-border bg-background"><p className="text-sm font-medium">{item.experiment}</p><p className="text-xs text-muted-foreground">{item.hypothesis}</p></div>
    )},
  ],
  reveal_insights: [
    { key: 'patterns_discovered', title: 'Patterns Discovered', icon: BarChart3, color: 'emerald', renderItem: (item) => (
      <div className="p-3 rounded-lg border border-border bg-background"><p className="text-sm font-medium">{item.pattern}</p><p className="text-xs text-muted-foreground">{item.strategic_implication}</p></div>
    )},
    { key: 'strategic_insights', title: 'Strategic Insights', icon: Lightbulb, color: 'amber', renderItem: (item) => (
      <div className="p-3 rounded-lg border border-border bg-background"><p className="text-sm font-medium">{item.insight}</p><span className={`text-[10px] px-1.5 py-0.5 rounded-full ${item.impact === 'high' ? 'bg-red-500/10 text-red-600' : 'bg-blue-500/10 text-blue-600'}`}>{item.impact} impact</span></div>
    )},
    { key: 'unexpected_findings', title: 'Unexpected Findings', icon: Star, color: 'violet', renderList: true },
  ],
  uncover_risks: [
    { key: 'risk_register', title: 'Risk Register', icon: Shield, color: 'red', renderItem: (item) => (
      <div className="p-3 rounded-lg border border-border bg-background">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-500/10 text-gray-600">{item.category}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${item.likelihood === 'high' ? 'bg-red-500/10 text-red-600' : 'bg-amber-500/10 text-amber-600'}`}>{item.likelihood} likelihood</span>
        </div>
        <p className="text-sm font-medium">{item.risk}</p>
        <p className="text-xs text-muted-foreground mt-1">Mitigation: {item.mitigation}</p>
        {item.early_warning && <p className="text-xs mt-0.5">Watch: {item.early_warning}</p>}
      </div>
    )},
    { key: 'critical_vulnerabilities', title: 'Critical Vulnerabilities', icon: AlertTriangle, color: 'red', renderList: true },
  ],
  identify_priorities: [
    { key: 'priority_matrix', title: 'Priority Matrix', icon: Target, color: 'violet', renderItem: (item) => (
      <div className="p-3 rounded-lg border border-border bg-background">
        <div className="flex items-start justify-between gap-2"><p className="text-sm font-medium flex-1">{item.initiative}</p><span className={`text-xs font-bold px-2 py-0.5 rounded-full shrink-0 ${item.category === 'quick_win' ? 'bg-emerald-500/10 text-emerald-600' : item.category === 'strategic_bet' ? 'bg-blue-500/10 text-blue-600' : item.category === 'deprioritize' ? 'bg-gray-500/10 text-gray-500' : 'bg-amber-500/10 text-amber-600'}`}>{item.category?.replace(/_/g, ' ')}</span></div>
        {item.overall_score && <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-violet-500 rounded-full" style={{ width: `${item.overall_score}%` }} /></div>}
      </div>
    )},
    { key: 'quick_wins', title: 'Quick Wins', icon: Zap, color: 'emerald', renderItem: (item) => (
      <div className="p-3 rounded-lg border border-border bg-background"><p className="text-sm font-medium">{item.action}</p><p className="text-xs text-muted-foreground">{item.expected_impact} &middot; {item.timeline?.replace(/_/g, ' ')}</p></div>
    )},
    { key: 'deprioritize', title: 'Stop Doing', icon: AlertTriangle, color: 'red', renderList: true },
  ],
};

function FocusReport({ report, mode, onBack, onAdvisory }) {
  if (!report) return null;
  const modeConfig = FOCUS_MODES.find(m => m.id === mode) || FOCUS_MODES[0];
  const Icon = modeConfig.icon;
  const sections = REPORT_SECTIONS[mode] || [];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6" data-testid="focus-report">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="w-4 h-4" /> Back</button>
      <div className="text-center">
        <div className={`w-12 h-12 rounded-xl ${modeConfig.bgColor} flex items-center justify-center mx-auto mb-3`}><Icon className={`w-6 h-6 ${modeConfig.textColor}`} /></div>
        <h1 className="text-2xl font-bold mb-1">{report.title || modeConfig.name}</h1>
      </div>

      {report.executive_summary && (
        <section className="rounded-xl border-2 border-indigo-500/30 bg-gradient-to-br from-indigo-500/5 to-purple-500/5 p-6">
          <h2 className="font-semibold flex items-center gap-2 mb-3"><Star className="w-5 h-5 text-indigo-500" /> Executive Summary</h2>
          <p className="text-base leading-relaxed">{report.executive_summary}</p>
        </section>
      )}

      {sections.map(sec => {
        const data = report[sec.key];
        if (!data || (Array.isArray(data) && data.length === 0)) return null;
        const SIcon = sec.icon;
        const colCls = sec.color === 'blue' ? 'text-blue-500' : sec.color === 'emerald' ? 'text-emerald-500' : sec.color === 'amber' ? 'text-amber-500' : sec.color === 'red' ? 'text-red-500' : 'text-violet-500';
        return (
          <section key={sec.key} className="rounded-xl border border-border bg-card p-6">
            <h2 className="font-semibold flex items-center gap-2 mb-4"><SIcon className={`w-5 h-5 ${colCls}`} /> {sec.title}</h2>
            {sec.renderList ? (
              <ul className="space-y-1.5">{data.map((item, i) => <li key={i} className="text-sm flex items-start gap-2"><div className={`w-1.5 h-1.5 rounded-full ${colCls} mt-2 shrink-0`} />{typeof item === 'string' ? item : item.step || item.action || JSON.stringify(item)}</li>)}</ul>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">{data.map((item, i) => <div key={i}>{sec.renderItem(item)}</div>)}</div>
            )}
          </section>
        );
      })}

      {/* Next steps / recommended actions */}
      {report.recommended_next_steps?.length > 0 && (
        <section className="rounded-xl border border-indigo-500/20 bg-card p-6">
          <h2 className="font-semibold flex items-center gap-2 mb-4"><Zap className="w-5 h-5 text-indigo-500" /> Recommended Next Steps</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {report.recommended_next_steps.map((s, i) => (
              <div key={i} className="p-3 rounded-lg border border-border bg-background">
                <div className="flex items-center gap-2 mb-1"><span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${s.priority === 'high' ? 'bg-red-500/10 text-red-600' : 'bg-blue-500/10 text-blue-600'}`}>{s.priority}</span></div>
                <p className="text-sm font-medium">{s.step}</p>
                {s.timeline && <p className="text-xs text-muted-foreground mt-0.5">{s.timeline.replace(/_/g, ' ')}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA: Continue with Advisory Board */}
      <div className="text-center pt-4 space-y-3">
        <div className="p-6 rounded-xl border-2 border-violet-500/30 bg-gradient-to-r from-violet-500/5 to-purple-500/5">
          <h3 className="font-semibold text-lg mb-2">Continue with Advisory Board</h3>
          <p className="text-sm text-muted-foreground mb-4">Have your advisory board debate these findings and provide collective recommendations.</p>
          <Button onClick={onAdvisory} className="bg-violet-600 hover:bg-violet-700 text-white px-6" data-testid="focus-to-advisory-btn">
            <Users className="w-4 h-4 mr-2" /> Discuss with Advisory Board
          </Button>
        </div>
        <Button onClick={onBack} variant="outline">Start Another Analysis</Button>
      </div>
    </div>
  );
}

// ── Main Sharpen Focus Component ──
export function SharpenFocus({ onBack, onAdvisory }) {
  const [subView, setSubView] = useState('modes'); // modes | chat | report
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeMode, setActiveMode] = useState(null);
  const [initialQuestion, setInitialQuestion] = useState('');
  const [reportData, setReportData] = useState(null);
  const [starting, setStarting] = useState(false);

  const handleModeSelect = async (modeId) => {
    setStarting(true);
    try {
      const res = await apiPost('/focus/sessions', { mode: modeId });
      if (res.ok) {
        const data = await res.json();
        setActiveSessionId(data.session_id);
        setActiveMode(modeId);
        setInitialQuestion(data.question);
        setSubView('chat');
      } else toast.error('Failed to start analysis');
    } catch { toast.error('Connection error'); }
    setStarting(false);
  };

  if (starting) return <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>;

  switch (subView) {
    case 'modes': return <FocusModeSelection onSelect={handleModeSelect} onBack={onBack} />;
    case 'chat': return <FocusChat sessionId={activeSessionId} mode={activeMode} initialQuestion={initialQuestion} onReport={(r, m) => { setReportData(r); setActiveMode(m); setSubView('report'); }} onBack={() => setSubView('modes')} />;
    case 'report': return <FocusReport report={reportData} mode={activeMode} onBack={() => setSubView('modes')} onAdvisory={onAdvisory} />;
    default: return <FocusModeSelection onSelect={handleModeSelect} onBack={onBack} />;
  }
}
